/**
 * TextAssetsSection Component
 * Manages text assets (headlines, descriptions, sitelinks, callouts, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';
import { AIRecommendationsButton } from '@/components/ui/AIRecommendationsButton';
import { getAIButtonStatus } from '@/components/ui/aiButtonStatus';
import { AddManuallyButton } from '@/components/ui/AddManuallyButton';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { BulkManagementTable } from '@/components/common/BulkManagementTable';
import {
  StatusMessage,
  RecommendationsTable,
  RelevanceBar,
  SaveActionBar,
  type TableColumn,
} from '@/components/common/Recommendations';
import {
  generateAssetRecommendations,
  saveSelectedAssets,
  getCampaignAssets,
  updateAssetStatus,
  type RecommendedAsset,
  type CampaignAsset,
} from '@/services/api/assets';
import type { ManagedItem } from '@/components/common/BulkManagementTable';
import { SitelinkUrlDialog } from './SitelinkUrlDialog';

// Extended type for displayed assets that satisfies ManagedItem requirements
type DisplayedAsset = CampaignAsset & { id: string } & ManagedItem;
import { useRecommendationSelection } from '@/hooks/recommendations/useRecommendationSelection';
import { debug, toast } from '@/utils';
import type {
  TextAssetType,
  EntityType,
  AssetGroupData,
  CampaignLevelAsset,
} from '../types';
import { TEXT_ASSET_LABELS, TEXT_ASSET_LIMITS, CAMPAIGN_LEVEL_ID } from '../types';

interface TextAssetsSectionProps {
  campaignId: string;
  campaignType: string;
  googleAdsAccountId: string;
  entityType: EntityType;
  entityId: string;
  assetType: TextAssetType;
  memberUuid: string;
  // Campaign data for PMax assets from config
  campaign?: {
    campaign_id: string;
    google_ads_account_id?: string;
    asset_group_list?: AssetGroupData[];
    sitelinks?: CampaignLevelAsset[];
    callouts?: CampaignLevelAsset[];
    structured_snippets?: Array<{
      header?: string;
      values?: string[];
      asset_id?: string;
      status?: string;
    }>;
  };
  selectedLandingPages?: Map<string, boolean>;
  onAssetsUpdated?: () => void;
}

export function TextAssetsSection({
  campaignId,
  campaignType,
  googleAdsAccountId,
  entityType,
  entityId,
  assetType,
  memberUuid,
  campaign,
  selectedLandingPages,
  onAssetsUpdated,
}: TextAssetsSectionProps) {
  const queryClient = useQueryClient();
  const isPerformanceMax = campaignType.includes('PERFORMANCE_MAX');
  const isCampaignLevel = entityId === CAMPAIGN_LEVEL_ID;

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);

  // Auto-clear success and error messages after 5 seconds
  useEffect(() => {
    if (message?.type === 'success' || message?.type === 'error') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const [recommendations, setRecommendations] = useState<RecommendedAsset[]>([]);
  const [buttonStatus, setButtonStatus] = useState<string>('');
  const [campaignAssets, setCampaignAssets] = useState<CampaignAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [localAssetUpdates, setLocalAssetUpdates] = useState<Map<string, { status?: string; removed?: boolean }>>(new Map());
  const { selectedIndices, toggleItem, selectAll, deselectAll, clearSelection } = useRecommendationSelection();

  // Manual add state
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualAssetText, setManualAssetText] = useState('');
  const [manualSitelinkUrl, setManualSitelinkUrl] = useState('');
  const [isAddingManual, setIsAddingManual] = useState(false);

  // Sitelink URL state
  const [showSitelinkUrlDialog, setShowSitelinkUrlDialog] = useState(false);
  const [sitelinkUrls, setSitelinkUrls] = useState<Record<number, string>>({});
  const [pendingSitelinkAssets, setPendingSitelinkAssets] = useState<Array<{ text: string; confidence?: number }>>([]);

  const maxLength = TEXT_ASSET_LIMITS[assetType] || 90;

  // Load campaign-level assets from campaign config (sitelinks, callouts, structured_snippets)
  // Works for both PMax and non-PMax campaigns
  const loadCampaignLevelAssetsFromConfig = useCallback(() => {
    if (!campaign) return;

    const isCampaignLevelAsset = ['sitelink', 'callout', 'structured_snippet'].includes(assetType);
    if (!isCampaignLevelAsset) return;

    let assets: CampaignAsset[] = [];

    if (assetType === 'sitelink') {
      const sitelinks = campaign.sitelinks || [];
      assets = sitelinks.map(sitelink => ({
        asset_id: sitelink.asset_id || '',
        resource_name: `customers/${googleAdsAccountId}/assets/${sitelink.asset_id}`,
        text: sitelink.text,
        asset_type: 'sitelink' as const,
        field_type: 'SITELINK',
        status: sitelink.status || 'UNDEFINED',
        final_url: sitelink.final_url,
      }));
    } else if (assetType === 'callout') {
      const callouts = campaign.callouts || [];
      assets = callouts.map(callout => ({
        asset_id: callout.asset_id || '',
        resource_name: `customers/${googleAdsAccountId}/assets/${callout.asset_id}`,
        text: callout.text,
        asset_type: 'callout' as const,
        field_type: 'CALLOUT',
        status: callout.status || 'UNDEFINED',
      }));
    } else if (assetType === 'structured_snippet') {
      const snippets = campaign.structured_snippets || [];
      assets = snippets.map(snippet => ({
        asset_id: snippet.asset_id || '',
        resource_name: `customers/${googleAdsAccountId}/assets/${snippet.asset_id}`,
        text: `${snippet.header}: ${(snippet.values || []).join(', ')}`,
        asset_type: 'structured_snippet' as const,
        field_type: 'STRUCTURED_SNIPPET',
        status: (snippet.status as CampaignAsset['status']) || 'UNDEFINED',
      }));
    }

    setCampaignAssets(assets);
    debug.log(`Loaded ${assets.length} ${assetType} asset(s) from campaign config`);
  }, [campaign, assetType, googleAdsAccountId]);

  // Load PMax asset-group-level assets from config (headlines, descriptions, long_headlines)
  const loadPMaxAssetGroupAssetsFromConfig = useCallback(() => {
    if (!isPerformanceMax || !campaign) return;

    const isCampaignLevelAsset = ['sitelink', 'callout', 'structured_snippet'].includes(assetType);
    if (isCampaignLevelAsset) return; // Handled by loadCampaignLevelAssetsFromConfig

    let assets: CampaignAsset[] = [];

    // Asset-group-level text assets (headlines, descriptions, long_headlines)
    const assetGroup = campaign.asset_group_list?.find(ag => ag.asset_group_id === entityId);
    if (assetGroup?.asset_list) {
      // Map asset types to their field type names
      const assetTypeMap: Record<string, string[]> = {
        'headline': ['HEADLINE', 'headline'],
        'description': ['DESCRIPTION', 'description'],
        'long_headline': ['LONG_HEADLINE', 'long_headline'],
      };
      const validTypes = assetTypeMap[assetType] || [];

      assets = assetGroup.asset_list
        .filter(asset => {
          const type = (asset.type || asset.asset_type || '').toUpperCase();
          return validTypes.some(t => type.includes(t.toUpperCase()));
        })
        .map(asset => ({
          asset_id: asset.asset_id || '',
          resource_name: `customers/${googleAdsAccountId}/assets/${asset.asset_id}`,
          text: asset.content || asset.text || asset.asset_text || '',
          asset_type: assetType as CampaignAsset['asset_type'],
          field_type: assetType.toUpperCase(),
          status: (asset.status as CampaignAsset['status']) || 'UNDEFINED',
        }));
    }

    setCampaignAssets(assets);
    debug.log(`Loaded ${assets.length} ${assetType} asset(s) from asset group config`);
  }, [isPerformanceMax, campaign, assetType, googleAdsAccountId, entityId]);

  // Fetch ad-group-level assets
  const fetchAdGroupAssets = useCallback(async () => {
    if (!entityId || isCampaignLevel || !memberUuid) return;

    setLoadingAssets(true);
    try {
      const response = await getCampaignAssets({
        member_uuid: memberUuid,
        ad_group_id: entityId,
        campaign_id: campaignId,
        customer_id: googleAdsAccountId,
        asset_type: assetType,
      });

      if (response.success) {
        setCampaignAssets(response.assets);
        debug.log(`Fetched ${response.count} asset(s)`);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  }, [entityId, isCampaignLevel, memberUuid, campaignId, googleAdsAccountId, assetType]);

  // Load assets based on entity type and asset type
  useEffect(() => {
    const isCampaignLevelAsset = ['sitelink', 'callout', 'structured_snippet'].includes(assetType);

    if (isCampaignLevelAsset && isCampaignLevel) {
      // Campaign-level assets (sitelinks, callouts, structured_snippets) - works for all campaign types
      loadCampaignLevelAssetsFromConfig();
    } else if (isPerformanceMax && !isCampaignLevelAsset) {
      // PMax: Load asset-group-level assets from config
      loadPMaxAssetGroupAssetsFromConfig();
    } else if (entityId && !isCampaignLevel) {
      // Non-PMax: Fetch ad-group-level assets from API
      fetchAdGroupAssets();
    }
  }, [entityId, assetType, isPerformanceMax, isCampaignLevel, loadCampaignLevelAssetsFromConfig, loadPMaxAssetGroupAssetsFromConfig, fetchAdGroupAssets]);

  // Clear selections when entity/type changes
  useEffect(() => {
    setSelectedAssets(new Set());
    setLocalAssetUpdates(new Map());
    setRecommendations([]);
    clearSelection();
    setButtonStatus('');
  }, [entityId, assetType, clearSelection]);

  // Generate AI recommendations
  const handleGenerateRecommendations = async () => {
    if (!entityId) {
      toast.warning('Please select an entity first');
      return;
    }

    setLoading(true);
    setMessage(null);
    setButtonStatus('');
    setRecommendations([]);
    clearSelection();

    try {
      const actualEntityId = isCampaignLevel ? campaignId : entityId;
      const actualEntityType = isCampaignLevel ? 'campaign' : entityType;

      const response = await generateAssetRecommendations({
        member_uuid: memberUuid,
        campaign_id: campaignId,
        campaign_type: campaignType,
        entity_id: actualEntityId,
        entity_type: actualEntityType as 'asset_group' | 'ad_group' | 'campaign',
        asset_type: assetType,
        max_recommendations: 10,
        analyze_images: true,
      });

      if (response.recommendations.length === 0) {
        setButtonStatus(response.message || 'No recommendations generated');
      } else {
        setButtonStatus(`Generated ${response.recommendations.length} ${TEXT_ASSET_LABELS[assetType]?.toLowerCase() || 'assets'}`);
      }

      setRecommendations(response.recommendations);
    } catch (error) {
      console.error('Error generating recommendations:', error);
      const apiError = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorMsg = apiError.response?.data?.detail || apiError.message || 'Failed to generate recommendations.';

      // Landing page access errors should show as toast to avoid layout shift
      if (errorMsg.includes('landing page') || errorMsg.includes('Landing page')) {
        toast.error(errorMsg);
      } else {
        setMessage({ type: 'error', text: `Error: ${errorMsg}` });
      }
    } finally {
      setLoading(false);
    }
  };

  // Save assets
  const handleSaveAssets = async (assetsToSave: Array<{ text: string; asset_type: string; confidence?: number; final_url?: string }>) => {
    setSaving(true);
    setMessage(null);

    try {
      const actualEntityId = isCampaignLevel ? campaignId : entityId;
      const actualEntityType = isCampaignLevel ? 'campaign' : entityType;

      const response = await saveSelectedAssets({
        member_uuid: memberUuid,
        campaign_id: campaignId,
        campaign_type: campaignType,
        entity_id: actualEntityId,
        entity_type: actualEntityType as 'asset_group' | 'ad_group' | 'campaign',
        customer_id: googleAdsAccountId,
        selected_assets: assetsToSave,
        trigger_data_sync: true,
      });

      if (response.success) {
        // Add new assets to local state (optimistic update)
        if (response.successful_assets && response.successful_assets.length > 0) {
          const newAssets = response.successful_assets
            .filter((asset: { asset_id?: string }) => asset.asset_id)
            .map((asset: { asset_id?: string; resource_name?: string; text: string }) => ({
              asset_id: asset.asset_id!,
              resource_name: asset.resource_name || '',
              text: asset.text,
              asset_type: assetType,
              field_type: assetType.toUpperCase(),
              status: 'ENABLED' as const,
            }));

          if (newAssets.length > 0) {
            setCampaignAssets(prev => [...prev, ...newAssets]);
          }
        } else if (assetsToSave.length > 0) {
          // Fallback: If no successful_assets returned, add based on input
          // This handles cases where backend doesn't return asset IDs
          const tempAssets = assetsToSave.map((asset, index) => ({
            asset_id: `temp-${Date.now()}-${index}`,
            resource_name: '',
            text: asset.text,
            asset_type: assetType,
            field_type: assetType.toUpperCase(),
            status: 'ENABLED' as const,
          }));
          setCampaignAssets(prev => [...prev, ...tempAssets]);
        }

        toast.success(response.message);
        onAssetsUpdated?.();
        return response;
      } else {
        // Check for specific error messages from failed assets
        let errorMsg = response.message;
        if (response.failed_assets && response.failed_assets.length > 0) {
          const firstError = response.failed_assets[0]?.error;
          if (firstError) {
            errorMsg = firstError;
          }
        }
        setMessage({ type: 'warning', text: errorMsg });
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error saving assets:', error);
      const err = error as Error;
      setMessage({ type: 'error', text: err.message || 'Failed to save assets. Please try again.' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Save selected recommendations
  const handleSaveRecommendations = async () => {
    if (selectedIndices.size === 0) {
      toast.warning('Please select at least one recommendation');
      return;
    }

    const selectedAssetsList = Array.from(selectedIndices).map((index) => ({
      text: recommendations[index].text,
      asset_type: assetType,
      confidence: recommendations[index].confidence,
      final_url: recommendations[index].final_url, // Include URL if present (for sitelinks)
    }));

    // For sitelinks, check if URLs are provided in recommendations
    if (assetType === 'sitelink') {
      // Check if all selected sitelinks have URLs from recommendations
      const allHaveUrls = selectedAssetsList.every(asset => asset.final_url);

      if (allHaveUrls) {
        // URLs are pre-filled from recommendations, save directly
        try {
          await handleSaveAssets(selectedAssetsList);
          setRecommendations([]);
          clearSelection();
        } catch {
          // Error handled in handleSaveAssets
        }
        return;
      }

      // Some or all need manual URL entry - show dialog
      const defaultUrl = selectedLandingPages && selectedLandingPages.size > 0
        ? Array.from(selectedLandingPages.keys())[0]
        : '';

      const initialUrls: Record<number, string> = {};
      selectedAssetsList.forEach((asset, index) => {
        // Use recommendation's final_url if available, otherwise use default
        initialUrls[index] = asset.final_url || defaultUrl;
      });

      setPendingSitelinkAssets(selectedAssetsList);
      setSitelinkUrls(initialUrls);
      setShowSitelinkUrlDialog(true);
      return;
    }

    try {
      await handleSaveAssets(selectedAssetsList);
      setRecommendations([]);
      clearSelection();
    } catch {
      // Error handled in handleSaveAssets
    }
  };

  // Save sitelinks with URLs
  const handleSaveSitelinksWithUrls = async () => {
    for (let i = 0; i < pendingSitelinkAssets.length; i++) {
      if (!sitelinkUrls[i]?.trim()) {
        toast.error(`Please provide a URL for sitelink ${i + 1}`);
        return;
      }

      const url = sitelinkUrls[i].trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        toast.error(`URL must start with http:// or https://`);
        return;
      }
    }

    const sitelinksWithUrls = pendingSitelinkAssets.map((asset, index) => ({
      text: asset.text,
      asset_type: 'sitelink',
      confidence: asset.confidence,
      final_url: sitelinkUrls[index].trim(),
    }));

    try {
      await handleSaveAssets(sitelinksWithUrls);
      setRecommendations([]);
      clearSelection();
      setShowSitelinkUrlDialog(false);
      setPendingSitelinkAssets([]);
      setSitelinkUrls({});
    } catch {
      // Error handled
    }
  };

  // Add manual asset
  const handleAddManualAsset = async () => {
    if (!manualAssetText.trim()) {
      setMessage({ type: 'error', text: 'Please enter asset text' });
      return;
    }

    if (manualAssetText.length > maxLength) {
      setMessage({ type: 'error', text: `Asset text must be ${maxLength} characters or less` });
      return;
    }

    // Sitelinks require a URL
    if (assetType === 'sitelink') {
      if (!manualSitelinkUrl.trim()) {
        setMessage({ type: 'error', text: 'Please enter a URL for the sitelink' });
        return;
      }
      const url = manualSitelinkUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        setMessage({ type: 'error', text: 'URL must start with http:// or https://' });
        return;
      }
    }

    setIsAddingManual(true);
    try {
      const assetData: { text: string; asset_type: string; final_url?: string } = {
        text: manualAssetText.trim(),
        asset_type: assetType,
      };

      if (assetType === 'sitelink') {
        assetData.final_url = manualSitelinkUrl.trim();
      }

      await handleSaveAssets([assetData]);

      setManualAssetText('');
      setManualSitelinkUrl('');
      setShowManualAdd(false);

      // Refresh assets
      const isCampaignLevelAsset = ['sitelink', 'callout', 'structured_snippet'].includes(assetType);
      if (isCampaignLevelAsset && isCampaignLevel) {
        loadCampaignLevelAssetsFromConfig();
      } else if (isPerformanceMax && !isCampaignLevelAsset) {
        loadPMaxAssetGroupAssetsFromConfig();
      } else if (!isPerformanceMax && !isCampaignLevel) {
        fetchAdGroupAssets();
      }
    } catch {
      // Error handled
    } finally {
      setIsAddingManual(false);
    }
  };

  // Batch action handler
  const handleBatchAction = async (action: 'enable' | 'pause' | 'remove', itemIds: string[]) => {
    const actionName = action === 'enable' ? 'enabling' : action === 'pause' ? 'pausing' : 'removing';
    setMessage({ type: 'info', text: `${actionName.charAt(0).toUpperCase() + actionName.slice(1)} selected assets...` });

    try {
      const isAssetGroupAsset = isPerformanceMax && !isCampaignLevel;

      const baseParams = {
        member_uuid: memberUuid,
        customer_id: googleAdsAccountId,
        asset_ids: itemIds,
        action: action,
        trigger_data_sync: true,
      };

      const requestParams = isCampaignLevel
        ? { ...baseParams, campaign_id: campaignId }
        : isAssetGroupAsset
        ? { ...baseParams, asset_group_id: entityId }
        : { ...baseParams, ad_group_id: entityId, campaign_id: campaignId };

      const response = await updateAssetStatus(requestParams);

      // Only update local state for assets that actually succeeded
      // Don't apply optimistic updates for failed assets
      if (response.successful_count > 0) {
        const successfulAssetIds = new Set(
          response.successful_assets?.map(a => a.asset_id) || []
        );

        const updates = new Map(localAssetUpdates);
        itemIds.forEach(assetId => {
          // Only update if this asset was actually successful
          if (successfulAssetIds.has(assetId)) {
            if (action === 'remove') {
              updates.set(assetId, { removed: true });
            } else {
              updates.set(assetId, { status: action === 'enable' ? 'ENABLED' : 'PAUSED' });
            }
          }
        });
        setLocalAssetUpdates(updates);
      }

      // Show appropriate message based on results
      if (response.failed_count > 0 && response.successful_count === 0) {
        // All failed - show error with details from failed_assets
        const errorDetail = response.failed_assets?.[0]?.error || response.message;
        setMessage({ type: 'error', text: errorDetail });
      } else if (response.failed_count > 0) {
        // Partial success - show warning via toast, clear inline status
        setMessage(null);
        toast.warning(response.message);
      } else {
        // All succeeded - show success via toast, clear inline status
        setMessage(null);
        toast.success(response.message);
      }
      setSelectedAssets(new Set());

      await queryClient.invalidateQueries({ queryKey: ['campaign'] });
      onAssetsUpdated?.();
    } catch (error) {
      console.error('Batch action error:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : `Failed to ${action} asset(s)`
      });
      throw error;
    }
  };

  // Get displayed assets with optimistic updates
  // Transform to include 'id' property for BulkManagementTable compatibility
  const getDisplayedAssets = () => {
    return campaignAssets
      .filter(asset => {
        const update = localAssetUpdates.get(asset.asset_id);
        if (update?.removed) return false;
        if (asset.status === 'REMOVED') return false;
        return true;
      })
      .map(asset => {
        const update = localAssetUpdates.get(asset.asset_id);
        const baseAsset = update?.status
          ? { ...asset, status: update.status as 'ENABLED' | 'PAUSED' | 'REMOVED' }
          : asset;
        // Add 'id' property for BulkManagementTable (expects item.id)
        return { ...baseAsset, id: asset.asset_id };
      });
  };

  // Table columns for recommendations
  const tableColumns: TableColumn<RecommendedAsset>[] = [
    {
      header: TEXT_ASSET_LABELS[assetType] || 'Asset',
      key: 'text',
      minWidth: '16rem',
      render: (rec) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{rec.text}</p>
          {rec.reasoning && (
            <p className="text-xs text-gray-500 mt-1 italic">{rec.reasoning}</p>
          )}
        </div>
      ),
    },
    // Show Landing Page column for sitelink recommendations
    ...(assetType === 'sitelink' ? [{
      header: 'Landing Page',
      key: 'final_url',
      render: (rec: RecommendedAsset) => (
        rec.final_url ? (
          <a
            href={rec.final_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-xs"
            title={rec.final_url}
          >
            {rec.final_url.replace(/^https?:\/\//, '').slice(0, 40)}
            {rec.final_url.replace(/^https?:\/\//, '').length > 40 ? '...' : ''}
          </a>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )
      ),
    }] as TableColumn<RecommendedAsset>[] : []),
    {
      header: 'Length',
      key: 'length',
      render: (rec) => (
        <span className="text-sm text-gray-600">{rec.character_count} chars</span>
      ),
    },
    {
      header: 'Relevance',
      key: 'relevance',
      render: (rec) => (
        <RelevanceBar score={rec.relevance_score || rec.confidence} className="text-sm text-gray-600" />
      ),
    },
  ];

  const displayedAssets = getDisplayedAssets();

  return (
    <div className="space-y-4">
      {/* Status Message */}
      {message && (
        <StatusMessage type={message.type} message={message.text} />
      )}

      {/* Generate Recommendations Button - hidden for sitelinks (requires sub-page URL scraping) */}
      <ActionButtonGroup
        statusMessage={assetType !== 'sitelink' ? (getAIButtonStatus(loading, TEXT_ASSET_LABELS[assetType]?.toLowerCase() || assetType, buttonStatus) || undefined) : undefined}
        isLoading={loading}
        reserveStatusSpace={assetType !== 'sitelink'}
      >
        {assetType !== 'sitelink' && (
          <AIRecommendationsButton
            onClick={handleGenerateRecommendations}
            disabled={loading || !entityId}
            isLoading={loading}
          />
        )}
        <AddManuallyButton
          onClick={() => setShowManualAdd(!showManualAdd)}
          disabled={!entityId}
          isActive={showManualAdd}
        />
      </ActionButtonGroup>

      {/* Manual Add Form */}
      {showManualAdd && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Add {TEXT_ASSET_LABELS[assetType]}
          </h4>
          <div className="space-y-3">
            <div>
              <Input
                value={manualAssetText}
                onChange={(e) => setManualAssetText(e.target.value)}
                placeholder={`Enter ${assetType.replace(/_/g, ' ')} text (max ${maxLength} chars)`}
                maxLength={maxLength}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                {manualAssetText.length}/{maxLength} characters
              </p>
            </div>
            {assetType === 'sitelink' && (
              <div>
                <Input
                  value={manualSitelinkUrl}
                  onChange={(e) => setManualSitelinkUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  type="url"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Destination URL for this sitelink
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleAddManualAsset}
                disabled={isAddingManual || !manualAssetText.trim() || (assetType === 'sitelink' && !manualSitelinkUrl.trim())}
                variant="primary"
              >
                {isAddingManual ? 'Adding...' : 'Add'}
              </Button>
              <Button
                onClick={() => {
                  setShowManualAdd(false);
                  setManualAssetText('');
                  setManualSitelinkUrl('');
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sitelink URL Dialog */}
      <SitelinkUrlDialog
        isOpen={showSitelinkUrlDialog}
        onClose={() => {
          setShowSitelinkUrlDialog(false);
          setPendingSitelinkAssets([]);
          setSitelinkUrls({});
        }}
        onSave={handleSaveSitelinksWithUrls}
        sitelinks={pendingSitelinkAssets}
        urls={sitelinkUrls}
        onUrlChange={(index, url) => setSitelinkUrls({ ...sitelinkUrls, [index]: url })}
        isSaving={saving}
      />

      {/* Existing Assets Table */}
      {displayedAssets.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Current {TEXT_ASSET_LABELS[assetType]} ({displayedAssets.length})
          </h4>
          <BulkManagementTable<DisplayedAsset>
            items={displayedAssets as DisplayedAsset[]}
            columns={[
              {
                key: 'text',
                header: TEXT_ASSET_LABELS[assetType] || 'Asset',
                render: (asset) => (
                  <span className="text-sm">{asset.text}</span>
                ),
              },
              // Show Landing Page column for sitelinks
              ...(assetType === 'sitelink' ? [{
                key: 'final_url' as keyof DisplayedAsset,
                header: 'Landing Page',
                render: (asset: DisplayedAsset) => (
                  asset.final_url ? (
                    <a
                      href={asset.final_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-md"
                      title={asset.final_url}
                    >
                      {asset.final_url.replace(/^https?:\/\//, '').slice(0, 60)}
                      {asset.final_url.replace(/^https?:\/\//, '').length > 60 ? '...' : ''}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )
                ),
              }] : []),
            ]}
            selectedItems={selectedAssets}
            onToggleItem={(assetId) => {
              setSelectedAssets(prev => {
                const next = new Set(prev);
                if (next.has(assetId)) {
                  next.delete(assetId);
                } else {
                  next.add(assetId);
                }
                return next;
              });
            }}
            onSelectAll={() => {
              if (selectedAssets.size === displayedAssets.length) {
                setSelectedAssets(new Set());
              } else {
                setSelectedAssets(new Set(displayedAssets.map(a => a.id)));
              }
            }}
            onBulkAction={handleBatchAction}
            itemLabel={assetType}
          />
        </div>
      )}

      {/* Recommendations Table */}
      {recommendations.length > 0 && (
        <div className="mt-4">
          <RecommendationsTable
            items={recommendations}
            columns={tableColumns}
            selectedItems={selectedIndices}
            onToggleItem={toggleItem}
            onSelectAll={() => selectAll(recommendations.length)}
            onDeselectAll={deselectAll}
            itemCount={recommendations.length}
            itemLabel={TEXT_ASSET_LABELS[assetType] || 'Assets'}
          />
          <SaveActionBar
            selectedCount={selectedIndices.size}
            itemLabel={TEXT_ASSET_LABELS[assetType] || 'asset'}
            onSave={handleSaveRecommendations}
            isSaving={saving}
            disabled={selectedIndices.size === 0}
          />
        </div>
      )}

      {/* Loading state */}
      {loadingAssets && (
        <div className="flex flex-col items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <p className="mt-3 text-sm text-gray-500">
            Loading {entityType === 'campaign' ? 'campaign' : entityType === 'asset_group' ? 'asset group' : 'ad group'} asset(s)...
          </p>
        </div>
      )}
    </div>
  );
}

export default TextAssetsSection;
